// 3wda Branding & Interactive UI Enhancements for Frappe / ERPNext 16
// Professional, Modern & Enterprise Aesthetics with Dual English/Arabic Support

(function () {
	const SPLASH_STORAGE_KEY = "threewda_splashShown";
	let observer = null;
	let refreshScheduled = false;
	let splashRendered = false;
	let brandingInitialized = false;
	let brandingRetryScheduled = false;

	function getBranding() {
		return window.frappe?.boot?.threewda_branding || null;
	}

	function getLogoUrl() {
		const branding = getBranding();
		return branding?.logo_circle_url || branding?.logo_url || "/assets/threewda/images/logo_syrcle.png";
	}

	function getFaviconUrl() {
		const branding = getBranding();
		return branding?.favicon_url || getLogoUrl();
	}

	function getFullLogoUrl() {
		const branding = getBranding();
		return branding?.logo_2_url || branding?.logo_xl_url || branding?.logo_url || "/assets/threewda/images/logo-2.png";
	}

	function replaceImageSource(img, url) {
		if (!img || !url || img.dataset.threewdaPatched === "1") {
			return;
		}

		img.src = url;
		img.dataset.threewdaPatched = "1";

		if (!img.style.maxHeight) {
			img.style.maxHeight = "36px";
		}
	}

	function ensureFavicon(url) {
		if (!url) {
			return;
		}

		const rels = ["icon", "shortcut icon", "apple-touch-icon"];

		rels.forEach((rel) => {
			let link = document.querySelector(`link[rel="${rel}"]`);
			if (!link) {
				link = document.createElement("link");
				link.rel = rel;
				if (rel === "icon" || rel === "shortcut icon") {
					link.type = "image/x-icon";
				}
				document.head.appendChild(link);
			}
			link.href = url;
		});
	}

	function replaceKnownLogos() {
		const logoUrl = getLogoUrl();
		const fullLogoUrl = getFullLogoUrl();
		if (!logoUrl) {
			return;
		}

		// Standard circle/compact logos
		const selectors = [
			".app-logo",
			".navbar-brand img",
			".sidebar-header img",
			"img[src*='frappe-framework-logo']",
			"img[src*='frappe-logo']",
			"img[src*='frappe-favicon']",
			"img[src*='erpnext-logo']",
			"img[src*='erpnext']",
			".about-app-logo",
			".app-card img",
			".standard-sidebar-item img",
		];

		selectors.forEach((selector) => {
			document.querySelectorAll(selector).forEach((img) => replaceImageSource(img, logoUrl));
		});

		// Login page or large logo areas can use full brand logo
		document.querySelectorAll(".for-login .app-logo, .login-content .app-logo").forEach((img) => {
			replaceImageSource(img, fullLogoUrl);
			img.style.maxHeight = "72px";
		});
	}

	function syncBootBranding() {
		const logoUrl = getLogoUrl();
		const faviconUrl = getFaviconUrl();
		if (!window.frappe?.boot || !logoUrl) {
			return;
		}

		window.frappe.boot.app_logo_url = logoUrl;

		if (Array.isArray(window.frappe.boot.app_data)) {
			window.frappe.boot.app_data.forEach((app) => {
				app.app_logo_url = logoUrl;
			});
		}

		if (faviconUrl) {
			window.frappe.boot.favicon = faviconUrl;
		}
	}

	function injectNavbarBadge() {
		if (!document.querySelector(".navbar-collapse") || document.querySelector(".threewda-navbar-badge")) {
			return;
		}

		const navbarRight = document.querySelector(".navbar-collapse .navbar-right, .navbar-collapse");
		if (navbarRight) {
			const badge = document.createElement("div");
			badge.className = "threewda-navbar-badge";
			badge.title = "3wda ERPNext 16 Theme Active | عودة للتقنية";
			badge.innerHTML = `
				<span class="threewda-navbar-badge__pulse"></span>
				<span>3wda Enterprise</span>
			`;
			navbarRight.insertBefore(badge, navbarRight.firstChild);
		}
	}

	function injectLoginEnhancements() {
		const isLogin = document.body && (document.body.getAttribute("data-route") === "login" || document.querySelector(".for-login") || window.location.pathname.includes("login"));
		if (!isLogin) {
			return;
		}

		// Ensure login card has polished title & subtitle if not already customized
		const loginHeader = document.querySelector(".login-content .page-head, .for-login .login-header, .login-content .text-center");
		if (loginHeader && !document.querySelector(".threewda-login-subtitle")) {
			const subtitle = document.createElement("div");
			subtitle.className = "threewda-login-subtitle";
			subtitle.style.cssText = "margin-top: 0.5rem; font-size: 0.95rem; font-weight: 600; color: #64748b; text-align: center; letter-spacing: 0.02em;";
			subtitle.innerHTML = `عودة لإدارة الموارد الأحدث | <span style="color: #0f3b8c; font-weight: 700;">3wda Enterprise ERP 16</span>`;
			loginHeader.appendChild(subtitle);
		}
	}

	function checkRtlLanguage() {
		const lang = window.frappe?.boot?.lang || document.documentElement.getAttribute("lang") || "en";
		if (lang === "ar" || document.documentElement.getAttribute("dir") === "rtl") {
			document.body.setAttribute("dir", "rtl");
			document.documentElement.setAttribute("dir", "rtl");
		}
	}

	function scheduleRefresh() {
		if (refreshScheduled) {
			return;
		}

		refreshScheduled = true;
		window.requestAnimationFrame(() => {
			refreshScheduled = false;
			syncBootBranding();
			ensureFavicon(getFaviconUrl());
			replaceKnownLogos();
			injectNavbarBadge();
			injectLoginEnhancements();
			checkRtlLanguage();
		});
	}

	function showSplash() {
		const branding = getBranding();
		if (!branding?.splash_enabled || splashRendered) {
			return;
		}

		if (window.sessionStorage.getItem(SPLASH_STORAGE_KEY)) {
			return;
		}

		if (!document.body) {
			return;
		}

		splashRendered = true;
		const duration = branding.splash_duration_ms || 2500;
		const logoUrl = getFullLogoUrl();

		const splash = document.createElement("div");
		splash.id = "threewda-splash";
		splash.innerHTML = `
			<div class="threewda-splash__backdrop">
				<div class="threewda-splash__orb threewda-splash__orb--blue"></div>
				<div class="threewda-splash__orb threewda-splash__orb--green"></div>
			</div>
			<div class="threewda-splash__content">
				<div class="threewda-splash__logo-wrap">
					<div class="threewda-splash__glow"></div>
					<img class="threewda-splash__logo" src="${logoUrl}" alt="3wda Logo">
				</div>
				<div class="threewda-splash__dots" aria-hidden="true">
					<span></span><span></span><span></span>
				</div>
				<div class="threewda-splash__text">
					<p class="threewda-splash__title">3wda Technologies | عودة للتقنية</p>
					<p class="threewda-splash__subtitle">Enterprise Resource Planning 16 | نظام إدارة الموارد المتكامل</p>
				</div>
			</div>
		`;

		document.body.prepend(splash);

		window.setTimeout(() => {
			splash.classList.add("fade-out");
			window.setTimeout(() => {
				splash.remove();
				window.sessionStorage.setItem(SPLASH_STORAGE_KEY, "true");
			}, 500);
		}, duration);
	}

	function shouldRefreshForMutations(mutations) {
		const relevantSelectors = [
			".app-logo",
			".navbar-brand",
			".sidebar-header",
			".about-app-logo",
			".login-content",
			".for-login",
			".navbar-collapse",
		];

		for (const mutation of mutations) {
			for (const node of mutation.addedNodes) {
				if (!(node instanceof Element)) {
					continue;
				}

				if (relevantSelectors.some((selector) => node.matches?.(selector) || node.querySelector?.(selector))) {
					return true;
				}
			}
		}

		return false;
	}

	function initObserver() {
		if (!document.body) {
			return;
		}

		const brandingRoots = [
			...document.querySelectorAll(".navbar, header.navbar, .desk-sidebar, .layout-side-section, .for-login, .login-content"),
		].filter(Boolean);

		if (!brandingRoots.length) {
			if (observer) {
				observer.disconnect();
				observer = null;
			}
			return;
		}

		if (!observer) {
			observer = new MutationObserver((mutations) => {
				if (shouldRefreshForMutations(mutations)) {
					scheduleRefresh();
				}
			});
		} else {
			observer.disconnect();
		}

		brandingRoots.forEach((root) => {
			observer.observe(root, { childList: true, subtree: true });
		});
	}

	function queueBrandingRetry() {
		if (brandingRetryScheduled || getBranding()) {
			return;
		}

		brandingRetryScheduled = true;
		window.setTimeout(() => {
			brandingRetryScheduled = false;
			scheduleRefresh();
			showSplash();
			initObserver();
			queueBrandingRetry();
		}, 300);
	}

	function initBranding() {
		if (!brandingInitialized) {
			brandingInitialized = true;
			document.addEventListener("visibilitychange", scheduleRefresh);
			window.addEventListener("load", scheduleRefresh);

			if (window.jQuery) {
				jQuery(document).on("app_ready page-change toolbar_setup", () => {
					scheduleRefresh();
					initObserver();
				});
			}
		}

		scheduleRefresh();
		showSplash();
		initObserver();
		queueBrandingRetry();
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", initBranding, { once: true });
	} else {
		initBranding();
	}
})();
